<script setup lang="ts">
import type { GetBalanceReturnType } from '@wagmi/core/actions';

import { formatCurrency } from '@/utils/helpers';
import { computed, showCurrencyIcon } from '#imports';
import { useUserStore } from '~/stores/user/useUserStore';

type Props = {
    isActive?: boolean;
    balance: GetBalanceReturnType;
};

const props = defineProps<Props>();
const userStore = useUserStore();

const balanceFormatted = computed(() => formatCurrency({
    value: props.balance.value,
    currencyType: props.balance.symbol as User['network']
}));

const currencySymbol = computed(() => props.balance.symbol ? showCurrencyIcon(props.balance.symbol, userStore.chainId) : undefined);

</script>

<template>
  <div v-bind="$attrs"
       class="display-flex flex-row align-items-center gap-12 balance cursor-pointer"
       :class="props.isActive && 'balance--active'"
  >
    <div class="display-flex flex-row align-items-center gap-12">
      <IconMain key="content"
                :icon="currencySymbol"
                :size="24"
      />
      <p class="text-600-accent-numeric text-color-primary">
        {{ balanceFormatted }}
      </p>
    </div>
    <div class="balance__icon display-flex align-items-center justify-center">
      <IconMain icon="person"
                :size="18"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">

%outline {
  content: '';
  position: absolute;
  z-index: -1;
  left: -5px;
  width: calc(100% + 10px);
  height: calc(100% + 10px);
  transition: all 0.3s ease-in-out;
  border-radius: 30px;
  opacity: 0;
  background-color: var(--content-brand-aviation);
}

.balance {
  position: relative;
  padding: $spacing-100 $spacing-100 $spacing-100 $spacing-300;
  border: 4px solid var(--layer-floor-1);
  border-radius: 30px;
  background-color: var(--bg-secondary);
  caret-color: transparent;

  &::before {
    @extend %outline
  }

  &--active {
    &::before {
      opacity: 1;
    }
  }

  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: var(--content-brand-aviation);
    color: var(--content-primary);
  }

  &:hover {
    &::before {
      opacity: 1;
    }
  }

}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
